import { ApolloClient, ApolloLink, createHttpLink, from, InMemoryCache, NormalizedCacheObject } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "apollo-link-error";
import ApolloLinkTimeout from "apollo-link-timeout";

import { ErrorReporter, ErrorSeverity } from "../hooks/useErrorHandler";
import { timeoutSeconds } from "../util/constants";
import { env } from "../util/env";
import { Sans } from "../util/sdk";
import typePolicies from "./typePolicies";

export const authLink = setContext(async (_, { headers }) => {
  const tokens = await Sans.sdk.core.api.tokens.freshTokens();
  return {
    credentials: "include",
    headers: {
      ...headers,
      "X-ACCESS-TOKEN": tokens?.id ? `Bearer ${tokens.id}` : "",
    },
  };
});

export function createClient(errorHandler: ErrorReporter): ApolloClient<NormalizedCacheObject> {
  const httpLink = createHttpLink({ uri: env.VITE_GRAPHQL_URI });
  const timeoutLink = new ApolloLinkTimeout(timeoutSeconds * 1000);
  const retryLink = new RetryLink({ attempts: { max: 3 } });

  const errorLink = onError(({ graphQLErrors, networkError, response, operation }) => {
    if (operation.operationName === "SendTelemetry") return;

    let message: string;
    if (graphQLErrors?.length) {
      message = graphQLErrors
        .reduce((unique, error) => {
          if (!unique.includes(error.message)) {
            unique.push(error.message);
          }
          return unique;
        }, [] as string[])
        .join("\n");
      message = `Server error: ${message}`;
    } else if (networkError) {
      message = `Network error: ${networkError.message}`;
    } else {
      message = "Unknown error";
    }

    let severity = ErrorSeverity.LOW;

    if (response) {
      response.errors = null as never;
      if (operation.operationName === "CourseQuery")
        severity = response?.data ? ErrorSeverity.MEDIUM : ErrorSeverity.HIGH;
    } else if (operation.operationName === "CourseQuery") {
      severity = ErrorSeverity.HIGH;
    }

    // Don't report errors for certain operations during initial load
    const isInitialLoadOperation = ["MyCoursesQuery", "CourseQuery", "AllMessages"].includes(operation.operationName);

    // Only report critical errors during initial load operations
    if (isInitialLoadOperation && severity !== ErrorSeverity.HIGH) {
      return;
    }

    errorHandler(severity, message, { ...operation });
  });

  const link = from([authLink, errorLink as unknown as ApolloLink, retryLink, timeoutLink, httpLink]);

  return new ApolloClient({
    defaultOptions: {
      query: { errorPolicy: "all" },
      mutate: { errorPolicy: "all" },
    },
    cache: new InMemoryCache({
      typePolicies,
    }),
    link,
    devtools: {
      enabled: true,
    },
  });
}
